import { TiChartSeriesComponent } from "../components/ti-chart-series.component";
import { ChartStyleColorModel } from "../models/chart-style/chart-style-color.model";
import { ChartStyleSettingsModel } from "../models/chart-style/chart-style-settings.model";
import { TIComparisonDataModel } from "../models/data-point/ti-comparison-data.model";
import { IndicatorColorType, TIIndicatorDataModel } from "../models/data-point/ti-indicator-data.model";
import { EMACloudSettingsModel } from "../models/ema-cloud/ema-cloud-settings.model";
import { Authorization } from "../models/enum/authorization.enum";
import { ChartType } from "../models/enum/chart-type.enum";
import { IndicatorType } from "../models/enum/indicator-type.enum";
import { HighChartBuilder } from "./highchart.builder";
const color = require('../library/darken-color.js');

export class HighChartStyleBuilder {
    private element: HTMLElement;
    private highChartBuilder: HighChartBuilder;

    private chartStylePrefix = '.ti-chart.normal';
    private isMarketingMode = false;
    private isPnPMode = false;
    private tiChartSeries: TiChartSeriesComponent;

    private chartId = '';
    constructor(tiChartSeries: TiChartSeriesComponent) {
        this.tiChartSeries = tiChartSeries;
        this.element = tiChartSeries.element;
        this.highChartBuilder = tiChartSeries.highChartBuilder;
        this.isMarketingMode = tiChartSeries.base.marketingWebChartMode;
        if (tiChartSeries.base.pnpMode) {
            this.isPnPMode = true;
            this.chartStylePrefix = '.ti-chart.pnp';
        }

        if (tiChartSeries.base.marketingWebChartMode) {
            this.chartStylePrefix += '.' + tiChartSeries.base.id;
        }

        this.chartId = tiChartSeries.base.id;
    }

    public createMainChartStyle(chartStyleSettings: ChartStyleSettingsModel) {
        let highChartStyle = this.element.querySelector('.ti-chart-style');
        if (highChartStyle) {
            highChartStyle.remove();
        }

        let styleTag = `<style>`;
        
        const hasSSW = this.tiChartSeries.base.element.querySelector('.single-stock-expand-btn');
        if (this.isMarketingMode && chartStyleSettings.previewChartBackgroundTrasparent) {
            styleTag += `${this.chartStylePrefix} { background: transparent; }`;
            styleTag += `${this.chartStylePrefix} .highcharts-background { fill:transparent; x: 0; y: 0; width: 100%; height: 100%; }`;
        } else {
            if (chartStyleSettings.isGradientChecked) {
                styleTag += `${this.chartStylePrefix} { background: linear-gradient(180deg, ${chartStyleSettings.selectedChartBackgroundColor.toRGB()}, ${chartStyleSettings.selectedChartGradientColor.toRGB()}); }`;
                if (hasSSW && !this.isPnPMode) {
                    styleTag += `${this.chartStylePrefix} .highcharts-background { fill:transparent; x: 0; y: 0; width: 100%; height: 100%; }`;
                } else {
                    styleTag += `${this.chartStylePrefix} .highcharts-background { fill:url(#tichart-bg${(this.isPnPMode ? '-pnp-' + this.chartId  : '-' + this.chartId )}); x: 0; y: 0; width: 100%; height: 100%; }`;
                }

                if (!this.isMarketingMode) {
                    document.body.setAttribute('style', `background: linear-gradient(180deg, ${chartStyleSettings.selectedChartBackgroundColor.toRGB()}, ${chartStyleSettings.selectedChartGradientColor.toRGB()});`);
                }
            } else {
                styleTag += `${this.chartStylePrefix} { background: ${chartStyleSettings.selectedChartBackgroundColor.toRGB()}; }`;
                styleTag += `${this.chartStylePrefix} .highcharts-background { fill:${chartStyleSettings.selectedChartBackgroundColor.toRGB()}; x: 0; y: 0; width: 100%; height: 100%; }`;
            
                if (!this.isMarketingMode) {
                    document.body.setAttribute('style', `background: ${chartStyleSettings.selectedChartBackgroundColor.toRGB()};`);
                }
            }
        }

        if (chartStyleSettings.chartType == ChartType.Candlestick) {
            styleTag += `${this.chartStylePrefix} .highcharts-candlestick-series.${this.highChartBuilder.mainSeriesClassName} .highcharts-point { stroke:${chartStyleSettings.selectedOutlineWickColor.toRGB()}; }`;

            if (chartStyleSettings.isOneColor) {
                styleTag += `${this.chartStylePrefix} .${this.highChartBuilder.mainSeriesClassName} .highcharts-point.one-color { fill:${chartStyleSettings.selectedLineColor.toRGB()}; }`;
            } else {
                styleTag += `${this.chartStylePrefix} .${this.highChartBuilder.mainSeriesClassName} .highcharts-point.up-color { fill:${chartStyleSettings.selectedUpCandleColor.toRGB()}; }`;
                styleTag += `${this.chartStylePrefix} .${this.highChartBuilder.mainSeriesClassName} .highcharts-point.down-color { fill:${chartStyleSettings.selectedDownCandleColor.toRGB()}; }`;
            }
        } else if (chartStyleSettings.chartType == ChartType.Bar) {
            if (chartStyleSettings.isOneColor) {
                styleTag += `${this.chartStylePrefix} .${this.highChartBuilder.mainSeriesClassName} .highcharts-point.one-color { fill:${chartStyleSettings.selectedLineColor.toRGB()}; stroke:${chartStyleSettings.selectedLineColor.toRGB()}; }`;
            } else {
                styleTag += `${this.chartStylePrefix} .${this.highChartBuilder.mainSeriesClassName} .highcharts-point.up-color { fill:${chartStyleSettings.selectedUpCandleColor.toRGB()}; stroke:${chartStyleSettings.selectedUpCandleColor.toRGB()}; }`;
                styleTag += `${this.chartStylePrefix} .${this.highChartBuilder.mainSeriesClassName} .highcharts-point.down-color { fill:${chartStyleSettings.selectedDownCandleColor.toRGB()}; stroke:${chartStyleSettings.selectedDownCandleColor.toRGB()}; }`;
            }
        } else if (chartStyleSettings.chartType == ChartType.Line) {
            if (chartStyleSettings.isOneColor) {
                styleTag += `${this.chartStylePrefix} .${this.highChartBuilder.mainSeriesClassName} .highcharts-point { fill:${chartStyleSettings.selectedLineColor.toRGB()}; }`;
                styleTag += `${this.chartStylePrefix} .${this.highChartBuilder.mainSeriesClassName} .highcharts-graph { stroke:${chartStyleSettings.selectedLineColor.toRGB()}; stroke-width:${chartStyleSettings.chartTypeLineWidth}px; }`;
            } else {
                styleTag += `${this.chartStylePrefix} .${this.highChartBuilder.mainSeriesClassName} .highcharts-graph.zone-up-color { stroke:${chartStyleSettings.selectedUpCandleColor.toRGB()}; stroke-width:${chartStyleSettings.chartTypeLineWidth}px; }`;
                styleTag += `${this.chartStylePrefix} .${this.highChartBuilder.mainSeriesClassName} .highcharts-graph.zone-down-color { stroke:${chartStyleSettings.selectedDownCandleColor.toRGB()}; stroke-width:${chartStyleSettings.chartTypeLineWidth}px; }`;

                styleTag += `${this.chartStylePrefix} .${this.highChartBuilder.mainSeriesClassName} .highcharts-point.zone-up-color { fill:${chartStyleSettings.selectedUpCandleColor.toRGB()}; stroke:${chartStyleSettings.selectedUpCandleColor.toRGB()}; }`;
                styleTag += `${this.chartStylePrefix} .${this.highChartBuilder.mainSeriesClassName} .highcharts-point.zone-down-color { fill:${chartStyleSettings.selectedDownCandleColor.toRGB()}; stroke:${chartStyleSettings.selectedDownCandleColor.toRGB()}; }`;
            }
        } else if (chartStyleSettings.chartType == ChartType.HLC || chartStyleSettings.chartType == ChartType.OHLC) {
            if (chartStyleSettings.isOneColor) {
                styleTag += `${this.chartStylePrefix} .${this.highChartBuilder.mainSeriesClassName} .highcharts-point.one-color { stroke:${chartStyleSettings.selectedLineColor.toRGB()}; stroke-width:${chartStyleSettings.chartTypeLineWidth}px; }`;
            } else {
                styleTag += `${this.chartStylePrefix} .${this.highChartBuilder.mainSeriesClassName} .highcharts-point.up-color { stroke:${chartStyleSettings.selectedUpCandleColor.toRGB()}; stroke-width:${chartStyleSettings.chartTypeLineWidth}px; }`;
                styleTag += `${this.chartStylePrefix} .${this.highChartBuilder.mainSeriesClassName} .highcharts-point.down-color { stroke:${chartStyleSettings.selectedDownCandleColor.toRGB()}; stroke-width:${chartStyleSettings.chartTypeLineWidth}px; }`;
            }
            
            if (this.tiChartSeries.hasAuthorization(Authorization.GoNoGo)) {
                for (let i = 0; i < chartStyleSettings.goLevelColors.length; i++) {
                    const color = chartStyleSettings.goLevelColors[i];
                    styleTag += `${this.chartStylePrefix} .${this.highChartBuilder.mainSeriesClassName} .highcharts-point.go-level-${i} { stroke:${color}; stroke-width:${chartStyleSettings.chartTypeLineWidth}px; }`;
                }
            }
        } else if (chartStyleSettings.chartType == ChartType.Area) {
            if (chartStyleSettings.isOneColor) {
                styleTag += `${this.chartStylePrefix} .${this.highChartBuilder.mainSeriesClassName} .highcharts-point { fill:none; stroke-width: 0; }`;
                styleTag += `${this.chartStylePrefix} .${this.highChartBuilder.mainSeriesClassName} .highcharts-graph { stroke:${chartStyleSettings.selectedLineColor.toRGB()}; stroke-width:${chartStyleSettings.chartTypeLineWidth}px; }`;

                if (chartStyleSettings.isGradientAreaColorChecked) {
                    styleTag += `${this.chartStylePrefix} .${this.highChartBuilder.mainSeriesClassName} .highcharts-area { fill-opacity: 1; stroke:${chartStyleSettings.selectedLineColor.toRGB()}; fill:url(#tichart-area-bg${(this.isPnPMode ? '-pnp-' + this.chartId  : '-' + this.chartId )}); }`;
                } else {
                    styleTag += `${this.chartStylePrefix} .${this.highChartBuilder.mainSeriesClassName} .highcharts-area { fill-opacity: 1; stroke:${chartStyleSettings.selectedLineColor.toRGB()}; fill:${chartStyleSettings.selectedLineColor.toRGBA(0.3)}; }`;
                }
            } else {
                styleTag += `${this.chartStylePrefix} .${this.highChartBuilder.mainSeriesClassName} .highcharts-area { fill-opacity: 0; }`;
                styleTag += `${this.chartStylePrefix} .${this.highChartBuilder.mainSeriesClassName} .highcharts-graph.zone-up-color { stroke:${chartStyleSettings.selectedUpCandleColor.toRGB()}; stroke-width:${chartStyleSettings.chartTypeLineWidth}px; }`;
                styleTag += `${this.chartStylePrefix} .${this.highChartBuilder.mainSeriesClassName} .highcharts-graph.zone-down-color { stroke:${chartStyleSettings.selectedDownCandleColor.toRGB()}; stroke-width:${chartStyleSettings.chartTypeLineWidth}px; }`;

                styleTag += `${this.chartStylePrefix} .${this.highChartBuilder.mainSeriesClassName} .highcharts-point.zone-up-color { fill:${chartStyleSettings.selectedUpCandleColor.toRGB()}; stroke:${chartStyleSettings.selectedUpCandleColor.toRGB()}; }`;
                styleTag += `${this.chartStylePrefix} .${this.highChartBuilder.mainSeriesClassName} .highcharts-point.zone-down-color { fill:${chartStyleSettings.selectedDownCandleColor.toRGB()}; stroke:${chartStyleSettings.selectedDownCandleColor.toRGB()}; }`;
            }
        }

        if (chartStyleSettings.isOneColor) {
            styleTag += `${this.chartStylePrefix} .tooltip.one-color { border-color:${chartStyleSettings.selectedLineColor.toRGB()}; }`;
            styleTag += `${this.chartStylePrefix} .tooltip.one-color .dot { background-color:${chartStyleSettings.selectedLineColor.toRGB()}; }`;
        } else {
            styleTag += `${this.chartStylePrefix} .tooltip.up-color { border-color:${chartStyleSettings.selectedUpCandleColor.toRGB()}; }`;
            styleTag += `${this.chartStylePrefix} .tooltip.up-color .dot { background-color:${chartStyleSettings.selectedUpCandleColor.toRGB()}; }`;
            
            styleTag += `${this.chartStylePrefix} .tooltip.down-color { border-color:${chartStyleSettings.selectedDownCandleColor.toRGB()}; }`;
            styleTag += `${this.chartStylePrefix} .tooltip.down-color .dot { background-color:${chartStyleSettings.selectedDownCandleColor.toRGB()}; }`;
        }

        styleTag += `${this.chartStylePrefix} .highcharts-xaxis-labels text {fill: ${chartStyleSettings.selectedDateTextColor.toRGB()}}`;
        styleTag += `${this.chartStylePrefix} .highcharts-yaxis-labels text {fill: ${chartStyleSettings.selectedPriceTextColor.toRGB()}}`;
        

        if (this.isMarketingMode && chartStyleSettings.timeframeColor) {
            styleTag += `${this.chartStylePrefix} .menu-box .menu-item.time ul li {color: ${chartStyleSettings.timeframeColor.toRGB()} !important;}`; 
            styleTag += `${this.chartStylePrefix} .menu-box .menu-item.time ul li.active {border-color: ${chartStyleSettings.timeframeColor.toRGB()} !important;}`; 
        }

        styleTag += `</style>`;

        const chartStyleContainer = `<div class="ti-chart-style">${styleTag}</div>`;
        this.element.insertAdjacentHTML('afterbegin', chartStyleContainer);
    }

    public createIndicatorStyle(rawDatas: Array<TIIndicatorDataModel>) {
        let highChartIndicatorsStyle = this.element.querySelector('.ti-chart-indicators-style');
        if (highChartIndicatorsStyle) {
            highChartIndicatorsStyle.remove();
        }

        if (this.tiChartSeries.base.pnpMode) return;
        let styleTag = `<style>`;
        for (const rawData of rawDatas) {
            if (rawData.abbreviatedName.indexOf('EMACLOUD') > -1) {                
                continue;
            }
            
            const needFill = rawData.chartType == 'column';
            const needStrokeWidth = rawData.chartType == 'line' || rawData.chartType == 'spline'; 
            const highChartIndicator = rawData.highChartIndicator;
            let className = rawData.getClassName();
            if (rawData.colorType == IndicatorColorType.One) {
                styleTag += `${this.chartStylePrefix} .${className} { stroke:${rawData.color} !important; ${needFill ? 'fill:' + rawData.color + ' !important;' : ''}}`;
                if (needStrokeWidth) {
                    styleTag += `${this.chartStylePrefix} .${className} .highcharts-graph { stroke-width: 1px;}`;
                }
            } else {
                for (let i = 0; i < rawData.colors.length; i++) {
                    styleTag += `${this.chartStylePrefix} .${rawData.colors[i].className} { stroke:${rawData.colors[i].color} !important; ${needFill ? 'fill:' + rawData.colors[i].color + ' !important;' : ''} ${needStrokeWidth ? 'stroke-width: 2px;' : ''}}`;
                }
            }

            if (highChartIndicator && rawData.indicatorType == IndicatorType.HighChartStochastic) {
                styleTag += `${this.chartStylePrefix} .${className} .highcharts-graph:first-child { stroke:${rawData.color} !important; }`;
            }

            if (rawData.childIndicators && rawData.childIndicators.length) {
                for (let i = 0; i < rawData.childIndicators.length; i++) {
                    const childRawData = rawData.childIndicators[i];
                    const childNeedFill = childRawData.chartType == 'column';
                    const childNeedStrokeWidth = childRawData.chartType  == 'line' || childRawData.chartType == 'spline'; 
                    const childClassName = childRawData.getClassName();
                    if (childRawData.colorType == IndicatorColorType.One) {
                        styleTag += `${this.chartStylePrefix} .${childClassName} { stroke:${childRawData.color} !important; ${childNeedFill ? 'fill:' + childRawData.color + ' !important;' : ''}}`;
                        if (childNeedStrokeWidth) {
                            styleTag += `${this.chartStylePrefix} .${childClassName} .highcharts-graph { stroke-width: 1px;}`;
                        }
                    } else {
                        for (let j = 0; j < childRawData.colors.length; j++) {
                            styleTag += `${this.chartStylePrefix} .${childRawData.colors[j].className} { stroke:${childRawData.colors[j].color} !important; ${childNeedFill ? 'fill:' + childRawData.colors[j].color + ' !important;' : ''}  ${childNeedStrokeWidth ? 'stroke-width: 2px;' : ''}}`;
                        }
                    }
                }
                
                if (highChartIndicator && rawData.indicatorType == IndicatorType.HighChartStochastic) {
                    styleTag += `${this.chartStylePrefix} .${className} .highcharts-graph:last-child { stroke:${rawData.childIndicators[0].color} !important; }`;
                }
            }
        }

        styleTag += `</style>`;

        const chartStyleContainer = `<div class="ti-chart-indicators-style">${styleTag}</div>`;
        this.element.insertAdjacentHTML('afterbegin', chartStyleContainer);
    }

    public createEMACloudStyle(model: EMACloudSettingsModel) {
        let highChartIndicatorsStyle = this.element.querySelector('.ti-chart-ema-cloud-style');
        if (highChartIndicatorsStyle) {
            highChartIndicatorsStyle.remove();
        }

        let styleTag = `<style>`;
        
        if (model?.items?.length) {
            for (let i = 0; i < model.items.length; i++) {
                const m = model.items[i];
                const upColor = m.upColor;
                const downColor = m.downColor;
                const className = `emacloud_${i}_mainchartarea`;
                let opacity = m.opacity / 100;
                if (m.opacity >= 100) {
                    opacity = 1;
                }
                
                styleTag += `${this.chartStylePrefix} .${className}ema-cloud-line-1 .highcharts-area { stroke:transparent; fill:transparent; }`;
                styleTag += `${this.chartStylePrefix} .${className}ema-cloud-line-1 .highcharts-area.zone-down-color { stroke:${downColor} !important; fill:${downColor} !important; fill-opacity: ${opacity} !important; }`;
                styleTag += `${this.chartStylePrefix} .${className}ema-cloud-line-1 .highcharts-area.zone-up-color { stroke:${upColor} !important; fill:${upColor} !important; fill-opacity: ${opacity} !important; }`;
                styleTag += `${this.chartStylePrefix} .${className}ema-cloud-line-1 .highcharts-point { stroke:transparent !important; fill:transparent !important; }`;
                styleTag += `${this.chartStylePrefix} .${className}ema-cloud-line-1 .highcharts-graph { stroke:transparent !important; fill:transparent !important; }`;
            }
        }
    
        styleTag += `</style>`;
        
        const chartStyleContainer = `<div class="ti-chart-ema-cloud-style">${styleTag}</div>`;
        this.element.insertAdjacentHTML('afterbegin', chartStyleContainer);
    }

    public createCrosshairsStyle(chartStyleSettings: ChartStyleSettingsModel) {
        let highChartStyle = this.element.querySelector('.ti-chart-cross-hairs-style');
        if (highChartStyle) {
            highChartStyle.remove();
        }

        let styleTag = `<style>`;
        if (chartStyleSettings.crossHairsEnabled) {
            styleTag += `${this.chartStylePrefix} .highchart-main-crosshair { display: block; stroke: #808080; stroke-dasharray: 1 2;}`;
        } else {
            styleTag += `${this.chartStylePrefix} .highchart-main-crosshair { stroke: transparent; opacity: 0; }`;
        }
        styleTag += `</style>`;
        
        if (this.tiChartSeries.chartStyleSettings) {
            this.tiChartSeries.chartStyleSettings.crossHairsEnabled = chartStyleSettings.crossHairsEnabled;
        }
        
        const chartStyleContainer = `<div class="ti-chart-cross-hairs-style">${styleTag}</div>`;
        this.element.insertAdjacentHTML('afterbegin', chartStyleContainer);
    }

    public createGridLinesStyle(chartStyleSettings: ChartStyleSettingsModel) {
        let highChartStyle = this.element.querySelector('.ti-chart-grid-lines-style');
        if (highChartStyle) {
            highChartStyle.remove();
        }

        let styleTag = `<style>`;
        if (!chartStyleSettings.gridLinesEnabled) {
            styleTag += `${this.chartStylePrefix}  .highcharts-xaxis-grid .highcharts-grid-line { opacity: 0; }`;
            styleTag += `${this.chartStylePrefix}  .highcharts-yaxis-grid .highcharts-grid-line { opacity: 0; }`;
        }
        
        styleTag += `</style>`;

        const chartStyleContainer = `<div class="ti-chart-grid-lines-style">${styleTag}</div>`;
        this.element.insertAdjacentHTML('afterbegin', chartStyleContainer);
    }

    public createPlotbandsStyle(chartStyleSettings: ChartStyleSettingsModel) {
        let highChartStyle = this.element.querySelector('.ti-chart-plot-bands-style');
        if (highChartStyle) {
            highChartStyle.remove();
        }

        let styleTag = `<style>`;
        if (chartStyleSettings.showPreMarket) {
            styleTag += `${this.chartStylePrefix}  .pre-market-plot-band { fill: ${chartStyleSettings.preMarketColor.toRGB()}; }`;
        } else {
            styleTag += `${this.chartStylePrefix}  .pre-market-plot-band { display: none; }`;
        }

        if (chartStyleSettings.showPostMarket) {
            styleTag += `${this.chartStylePrefix}  .post-market-plot-band { fill: ${chartStyleSettings.postMarketColor.toRGB()}; }`;
        } else {
            styleTag += `${this.chartStylePrefix}  .post-market-plot-band { display: none; }`;
        }

        if (chartStyleSettings.isDarkTheme) {
            styleTag += `${this.chartStylePrefix}  .earning-notification-plot-band { fill: ${chartStyleSettings.earningRectangleColor.toRGB()}; fill-opacity: 0.35; }`;
        } else {
            styleTag += `${this.chartStylePrefix}  .earning-notification-plot-band { fill: ${chartStyleSettings.earningRectangleColor.toRGB()}; fill-opacity: 0.4; }`;
        }

        styleTag += `</style>`;

        const chartStyleContainer = `<div class="ti-chart-plot-bands-style">${styleTag}</div>`;
        this.element.insertAdjacentHTML('afterbegin', chartStyleContainer);
    }

    public createComparisonChartStyle(tiComparisionsData: Array<TIComparisonDataModel>) {
        let highChartStyle = this.element.querySelector('.ti-comparison-chart-style');
        if (highChartStyle) {
            highChartStyle.remove();
        }

        if (!tiComparisionsData.length) {
            return;
        }

        let styleTag = `<style>`;
        
        for (const comparisonData of tiComparisionsData) {
            styleTag += `${this.chartStylePrefix} .comparison-series-${comparisonData.symbol} .highcharts-point { display: none; }`;
            styleTag += `${this.chartStylePrefix} .comparison-series-${comparisonData.symbol} .highcharts-graph { stroke:${comparisonData.color.toRGB()}; }`;
        }

        styleTag += `</style>`;

        const chartStyleContainer = `<div class="ti-comparison-chart-style">${styleTag}</div>`;
        this.element.insertAdjacentHTML('afterbegin', chartStyleContainer);
    }

    public getTIChartBackgroundSVGDefs(chartStyleSettings: ChartStyleSettingsModel): SVGLinearGradientElement | null {
        if (chartStyleSettings.isGradientChecked) {
            const defTags = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
            defTags.setAttribute('x1', '0');
            defTags.setAttribute('y1', '0');
            defTags.setAttribute('x2', '0');
            defTags.setAttribute('y2', '1');
            defTags.setAttribute('id', 'tichart-bg' + (this.isPnPMode ? '-pnp-' + this.chartId  : '-' + this.chartId ));

            const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
            stop1.setAttribute('offset', '0');
            stop1.setAttribute('stop-opacity', '1');
            stop1.setAttribute('stop-color', chartStyleSettings.selectedChartBackgroundColor.toRGB());
            defTags.appendChild(stop1);

            const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
            stop2.setAttribute('offset', '1');
            stop2.setAttribute('stop-opacity', '1');
            stop2.setAttribute('stop-color', chartStyleSettings.selectedChartGradientColor.toRGB());
            defTags.appendChild(stop2);
            
            return defTags;
        } 
        
        return null;
    }

    public getTIChartAreaBackgroundSVGDefs(chartStyleSettings: ChartStyleSettingsModel): SVGLinearGradientElement | null {
        if (chartStyleSettings.chartType == ChartType.Area && chartStyleSettings.isGradientAreaColorChecked) {
            if (this.isMarketingMode) {
                const lineColor1 = chartStyleSettings.marketingChartAreaFillHexColor;
                const lineColor2 = chartStyleSettings.marketingChartGradientHexColor;
    
                const defTags = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
                defTags.setAttribute('x1', '0');
                defTags.setAttribute('y1', '0');
                defTags.setAttribute('x2', '0');
                defTags.setAttribute('y2', '1');
                defTags.setAttribute('id', 'tichart-area-bg' + (this.isPnPMode ? '-pnp-' + this.chartId  : '-' + this.chartId ));
    
                const fillOpacity = 0.3;
                const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
                stop1.setAttribute('offset', '0');
                stop1.setAttribute('stop-opacity', '1');
                stop1.setAttribute('stop-color', lineColor1.toRGBA(fillOpacity));
                defTags.appendChild(stop1);
    
                const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
                stop2.setAttribute('offset', '1');
                stop2.setAttribute('stop-opacity', '1');
                stop2.setAttribute('stop-color', lineColor2.toRGBA(fillOpacity));
                defTags.appendChild(stop2);
            
                return defTags;
            } else {
                const lineColorHexLighten = chartStyleSettings.selectedLineColor.lighten(50);
                const lineColor2 = new ChartStyleColorModel(color.HEX2RGB(lineColorHexLighten));
    
                const defTags = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
                defTags.setAttribute('x1', '0');
                defTags.setAttribute('y1', '0');
                defTags.setAttribute('x2', '0');
                defTags.setAttribute('y2', '1');
                defTags.setAttribute('id', 'tichart-area-bg' + (this.isPnPMode ? '-pnp-' + this.chartId  : '-' + this.chartId ));
    
                const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
                stop1.setAttribute('offset', '0');
                stop1.setAttribute('stop-opacity', '1');
                stop1.setAttribute('stop-color', chartStyleSettings.selectedLineColor.toRGBA(0.3));
                defTags.appendChild(stop1);
    
                const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
                stop2.setAttribute('offset', '1');
                stop2.setAttribute('stop-opacity', '1');
                stop2.setAttribute('stop-color', lineColor2.toRGBA(0.3));
                defTags.appendChild(stop2);
            
                return defTags;
            }
        }

        return null;
    }

    public getMarketingWebChartPIPTIChartAreaBackgroundSVGDefs(chartStyleSettings: ChartStyleSettingsModel, colorOne: string, colorTwo: string): SVGLinearGradientElement | null {
        if (chartStyleSettings.chartType == ChartType.Area && chartStyleSettings.isGradientAreaColorChecked) {
            const defTags = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
            defTags.setAttribute('x1', '0');
            defTags.setAttribute('y1', '0');
            defTags.setAttribute('x2', '0');
            defTags.setAttribute('y2', '1');
            defTags.setAttribute('id', 'tichart-area-bg' + (this.isPnPMode ? '-pnp-' + this.chartId  : '-' + this.chartId ));

            const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
            stop1.setAttribute('offset', '0');
            stop1.setAttribute('stop-opacity', '1');
            stop1.setAttribute('stop-color', colorOne);
            defTags.appendChild(stop1);

            const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
            stop2.setAttribute('offset', '1');
            stop2.setAttribute('stop-opacity', '1');
            stop2.setAttribute('stop-color', colorTwo);
            defTags.appendChild(stop2);
        
            return defTags;
        }

        return null;
    }
}